import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import AppLayout from "@/components/AppLayout";

import Home from "@/pages/Home";
import { Login, Register, ForgotPassword, ResetPassword, VerifyEmail, AcceptInvite, AuthCallback } from "@/pages/Auth";
import Course from "@/pages/Course";
import Lesson from "@/pages/Lesson";
import Assessment from "@/pages/Assessment";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Support from "@/pages/Support";
import Profile from "@/pages/Profile";
import Mentorship from "@/pages/BookSession";
import AIProposalGenerator from "@/pages/AIProposalGenerator";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import OurStory from "@/pages/OurStory";
import Faqs from "@/pages/Faqs";
import JoinUs from "@/pages/JoinUs";
import ProfileSetup from "@/pages/ProfileSetup";
import Video from "@/pages/Video";
import BookSession from "@/pages/BookSession";
import Inbox from "@/pages/Inbox";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminContent from "@/pages/admin/AdminContent";
import AdminCommunity from "@/pages/admin/AdminCommunity";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminOwner from "@/pages/admin/AdminOwner";

function Protected({ children, staff = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading || user === undefined) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (staff && !(user.role === "admin" || user.role === "reviewer" || user.role === "support")) return <Navigate to="/" replace />;
  if (user.role === "student" && !user.profile_completed && loc.pathname !== "/profile-setup")
    return <Navigate to="/profile-setup" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/profile-setup" element={<Protected><ProfileSetup /></Protected>} />

      <Route element={<AppLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/book" element={<BookSession />} />
      </Route>

      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/course" element={<Course />} />
        <Route path="/lesson/:lessonId" element={<Lesson />} />
        <Route path="/phase/:phaseId/assessment" element={<Assessment />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/jobs/:jobId" element={<JobDetail />} />
        <Route path="/support" element={<Support />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/mentorship" element={<Mentorship />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/ai-proposal" element={<AIProposalGenerator />} />
        <Route path="/video" element={<Video />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:productId" element={<ProductDetail />} />
        <Route path="/our-story" element={<Navigate to="/my-story" replace />} />
        <Route path="/my-story" element={<OurStory />} />
        <Route path="/faqs" element={<Faqs />} />
        <Route path="/join-us" element={<JoinUs />} />
      </Route>

      <Route path="/admin" element={<Protected staff><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="students" element={<AdminStudents />} />
        <Route path="courses" element={<AdminCourses />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="jobs" element={<AdminJobs />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="community" element={<AdminCommunity />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="marketing" element={<AdminMarketing />} />
        <Route path="staff" element={<AdminStaff />} />
        <Route path="owner" element={<AdminOwner />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
